import React from 'react';
import { Box, SxProps, Theme } from '@mui/material';
import { cardSx } from './uiHelpers';

interface SurfaceCardProps {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  action?: React.ReactNode;
  children?: React.ReactNode;
  // Default 20px padding for the body; pass 0 for edge-to-edge lists and tables.
  bodyPadding?: number | string;
  sx?: SxProps<Theme>;
  onClick?: () => void;
}

// White (or dark) rounded card with an optional title row, matching the mockup sections.
export const SurfaceCard = ({ title, subtitle, action, children, bodyPadding = '20px', sx, onClick }: SurfaceCardProps) => {
  const hasHeader = title || subtitle || action;
  return (
    <Box sx={[cardSx, { minWidth: 0, cursor: onClick ? 'pointer' : undefined }, ...(Array.isArray(sx) ? sx : [sx])]} onClick={onClick}>
      {hasHeader && (
        <Box sx={{ p: '18px 20px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
            {title && <Box sx={{ fontSize: 15, fontWeight: 600 }}>{title}</Box>}
            {subtitle && <Box sx={{ fontSize: 13, color: 'var(--muted)' }}>{subtitle}</Box>}
          </Box>
          {action}
        </Box>
      )}
      {children !== undefined && <Box sx={{ p: hasHeader && bodyPadding !== 0 ? `0 20px 20px` : bodyPadding }}>{children}</Box>}
    </Box>
  );
};

// A row inside a SurfaceCard list, separated by a top border.
export const CardRow = ({ children, onClick, sx }: { children: React.ReactNode; onClick?: () => void; sx?: SxProps<Theme> }) => (
  <Box
    onClick={onClick}
    sx={[
      {
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: '12px 20px',
        borderTop: '1px solid var(--border)',
        cursor: onClick ? 'pointer' : undefined,
        '&:hover': onClick ? { backgroundColor: 'var(--muted-bg)' } : undefined,
      },
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
  >
    {children}
  </Box>
);
